import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsMore from "highcharts/highcharts-more";
import { Box, Card, Stack, Typography, Grid } from "@mui/material";
import moment from "moment";
import { useParams } from "react-router-dom";
import { useGetLastUpdatedOnStatusQuery } from "store/slices/apis/projectStatusApiSlice";
import { overviewChartPalette } from "styles/theme";

const LegendItem = ({ color, label, dashed = false }) => (
  <Stack
    direction="row"
    spacing={1.2}
    alignItems="center"
    sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
    title={label}
  >
    <Box
      sx={{
        width: "28px",
        height: "3px",
        borderRadius: 999,
        bgcolor: color,
        ...(dashed && {
          background: `repeating-linear-gradient(
            to right,
            ${color},
            ${color} 6px,
            transparent 6px,
            transparent 10px
          )`,
        }),
      }}
    />
    <Typography
      variant="body2"
      sx={{
        fontSize: "12px",
        fontWeight: 500,
        color: "#4B5563",
        letterSpacing: "0.5px",
        fontFamily: "Outfit, sans-serif",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
  </Stack>
);


const SCurveCostManagement = ({ kpiData, milestoneData }) => {
  const { projectId } = useParams()
  const { data: scheduleDate } = useGetLastUpdatedOnStatusQuery({ ProjectId: projectId });

  const rows = kpiData?.data ?? [];
  const milestones = Array.isArray(milestoneData)
    ? milestoneData
    : (milestoneData?.data ?? []);

  // X-axis categories
  const categories = rows.map((r) => moment(r?.month)?.format('MMM-YY') || "");
  const statusLabel = scheduleDate?.scheduleUpdatedDate
    ? moment(scheduleDate.scheduleUpdatedDate).format("MMM-YY")
    : null;

  // const statusIndex = categories.findIndex(c => c === statusLabel);
  const statusIndex = statusLabel
    ? categories.findIndex((c) => c === statusLabel)
    : -1;

  // 👉 Keep nulls as null – do NOT coerce to 0
  const planSeries = rows.map((r) =>
    r.plan === null || r.plan === undefined ? null : Number(r.plan)
  );
  const totalPlan = rows.reduce(
    (sum, r) => r.plan != null ? Number(r.plan) : sum,
    0
  );
  //console.log("totalPlan", totalPlan);

  const evSeries = rows.map((r) =>
    r.ev === null || r.ev === undefined ? null : Number(r.ev)
  );
  const totalEV = rows.reduce(
    (sum, r) => r.ev != null ? sum + Number(r.ev) : sum,
    0
  );
  //console.log("totalEV", totalEV);

  const evCumSeries = rows.map((r) =>
    r.eV_Cum_WithForecast === null || r.eV_Cum_WithForecast === undefined ? null : Number(r.eV_Cum_WithForecast)
  );
  const totalEVCum = rows.reduce(
    (sum, r) => r.eV_Cum_WithForecast != null ? sum + Number(r.eV_Cum_WithForecast) : sum,
    0
  );
  //console.log("totalEVCum", totalEVCum);

  const acwpSeries = rows.map((r) =>
    r.acwp === null || r.acwp === undefined ? null : Number(r.acwp)
  );
  const totalACWP = rows.reduce(
    (sum, r) => r.acwp != null ? sum + Number(r.acwp) : sum,
    0
  );
  //console.log("totalACWP", totalACWP);    

  const acwpCumSeries = rows.map((r) =>
    r.acwP_Cum_WithForecast === null || r.acwP_Cum_WithForecast === undefined ? null : Number(r.acwP_Cum_WithForecast)
  );
  const totalACWPCum = rows.reduce(
    (sum, r) => r.acwP_Cum_WithForecast != null ? sum + Number(r.acwP_Cum_WithForecast) : sum,
    0
  );
  //console.log("totalACWPCum", totalACWPCum);

  const bacSeries = rows.map((r) =>
    r.bac === null || r.bac === undefined ? null : Number(r.bac)
  );
  const totalBAC = rows.reduce(
    (sum, r) => r.bac != null ? sum + Number(r.bac) : sum,
    0
  );
  console.log("totalBAC", totalBAC);

  const bacPointSeries = rows.map((r, idx) =>
    idx === rows.length - 1 ? Number(r.bac) : null
  );
  //console.log("bac serires",bacPointSeries);

  // const etcSeries = rows.map((r) =>
  //   r.etc === null || r.etc === undefined ? null : Number(r.etc)
  // );  
  // const totalETC = rows.reduce(
  //   (sum, r) => r.etc != null ? sum + Number(r.etc) : sum,
  //   0
  // );
  // console.log("totalETC", totalETC);

  // const eacSeries = rows.map((r) =>
  //   r.eac === null || r.eac === undefined ? null : Number(r.eac)
  // );  
  const totalEAC = rows.reduce(
    (sum, r) => r.eac != null ? Number(r.eac) : sum,
    0
  );
  //console.log("totalEAC", totalEAC);  

  //console.log("totalBAC", totalBAC);
  var maxEac = 0;
  const eacPointSeries = rows.map((r, idx) => {
    if (r.eac > 0) {
      maxEac = r.eac;
    }
    return idx === rows.length - 1 ? Number(maxEac) : null;
  });
  //console.log("eac serires",eacPointSeries);

  // 👉 Keep nulls as null – do NOT coerce to 0
  const actualSeries = rows.map((r) =>
    r.actual === null || r.actual === undefined ? null : Number(r.actual)
  );

  // 👉 BCWP = Actual, same null behaviour so the line stops where actual becomes null
  const bcwpSeries = actualSeries.slice();


  const projectedSeries = rows.map((r) =>
    r.eV_Cum_WithForecast === null || r.eV_Cum_WithForecast === undefined ? null : Number(r.eV_Cum_WithForecast)
  );

  const gapPositive = rows.map((_, i) => {
    const pv = planSeries[i];
    const ev = evSeries[i];
    if (pv == null || ev == null) return null;
    return pv >= ev ? [ev, pv] : null;   // fill EV -> PV when PV above
  });

  const gapNegative = rows.map((_, i) => {
    const pv = planSeries[i];
    const ev = evSeries[i];
    if (pv == null || ev == null) return null;
    return ev > pv ? [pv, ev] : null;    // fill PV -> EV when EV above
  });

  const lastBAC = rows.slice().reverse().find(r => r.bac != null)?.bac;
  const lastEAC = rows.slice().reverse().find(r => r.eac != null)?.eac;

  const allY = [
    ...planSeries, ...evSeries, ...acwpSeries,
    lastBAC != null ? Number(lastBAC) : null,
    lastEAC != null ? Number(lastEAC) : null,
  ].filter(v => v != null);
  const bacPoint = lastBAC != null ? [{ x: categories.length - 1, y: Number(lastBAC) }] : [];
  const eacPoint = lastEAC != null ? [{ x: categories.length - 1, y: Number(lastEAC) }] : [];


  const yMax = Math.max(...allY) * 1.03// 12% headroom

  const statusRow =
    statusIndex >= 0 && statusIndex < rows.length ? rows[statusIndex] : null;

  // Prefer CUM at status (these exist on your status month row)
  const evAtStatus =
    statusRow?.ev != null ? Number(statusRow.ev) :
      statusRow?.eV_Cum_WithForecast != null ? Number(statusRow.eV_Cum_WithForecast) :
        null;

  const acwpAtStatus =
    statusRow?.acwp != null ? Number(statusRow.acwp) :
      statusRow?.acwP_Cum_WithForecast != null ? Number(statusRow.acwP_Cum_WithForecast) :
        null;

  // default color if we can't compute
  console.log(evAtStatus, acwpAtStatus, "evAtStatus, acwpAtStatus");
  const actualColor =
    evAtStatus != null && acwpAtStatus != null
      ? (acwpAtStatus <= evAtStatus ? overviewChartPalette.teal : overviewChartPalette.coral)
      : overviewChartPalette.coral;
  console.log("actualColor", actualColor);

  const lastNonNullIndex = (arr) => {
    for (let i = arr.length - 1; i >= 0; i--) {
      if (arr[i] != null && !Number.isNaN(arr[i])) return i;
    }
    return -1;
  };
  const withLastPointOnly = (arr, lastIdx, pointCfg = {}) =>
    arr.map((y, i) => {
      if (y == null) return null;
      return i === lastIdx
        ? { y, ...pointCfg }
        : y; // keep as number for all other points
    });
  const bacLastIdx = lastNonNullIndex(bacSeries);
  const projLastIdx = lastNonNullIndex(acwpCumSeries);

  const bacLastY = bacLastIdx >= 0 ? Number(bacSeries[bacLastIdx]) : null;
  const projLastY = projLastIdx >= 0 ? Number(acwpCumSeries[projLastIdx]) : null;

  const yRange = yMax || 1;
  const closeThreshold = yRange * 0.03; // 3% of chart range (tweak 0.02–0.05)

  const areClose =
    bacLastY != null && projLastY != null && Math.abs(bacLastY - projLastY) <= closeThreshold;

  // If close, separate them: higher one goes up, lower one goes down
  let bacLabelY = -10;
  let projLabelY = -10;

  if (areClose) {
    if (bacLastY >= projLastY) {
      bacLabelY = -18;   // BAC above
      projLabelY = 28;   // Projected below
    } else {
      bacLabelY = 28;    // BAC below
      projLabelY = -18;  // Projected above
    }
  }

  const bacSeriesLastPoint = withLastPointOnly(bacSeries, bacLastIdx, {
    marker: { enabled: true, radius: 6 },
    dataLabels: {
      enabled: true,
      format: "BAC-{y:.2f} Cr",
      crop: false,
      overflow: "none",
      style: { fontWeight: "700", textOutline: "none" },
      y: bacLabelY,
    },
  });

  const projSeriesLastPoint = withLastPointOnly(acwpCumSeries, projLastIdx, {
    marker: { enabled: true, radius: 6 },
    dataLabels: {
      enabled: true,
      format: "{y:.2f} Cr",
      crop: false,
      overflow: "none",
      style: { fontWeight: "700", textOutline: "none" },
      y: projLabelY,
    },
  });

  // --- Milestones -> map to month bucket (same as your xAxis categories)
  const milestoneMonthLabel = (iso) => moment(iso).format("MMM-YY");

  // Map milestones to x index (category index)
  const milestonePoints = (milestones ?? [])
    .map((m) => {
      const dateForPlot = m.curePeriodDueDate ?? m.dueDate; // ✅ use cure period
      if (!dateForPlot) return null;

      const label = milestoneMonthLabel(dateForPlot);
      const x = categories.findIndex((c) => c === label);
      if (x < 0) return null;

      return {
        x,
        y: Number(m.milestoneValue ?? 0),
        name: m.milestone,
        status: m.status,
        dueDate: m.dueDate,
        curePeriodDueDate: m.curePeriodDueDate,
        percentageComplete: m.percentageComplete,
      };
    })
    .filter(Boolean);

  // Plot lines for each milestone
  // count milestones per x so we can stack badges if same month
  const xCounts = {};
  const milestonePlotLines = milestonePoints.map((p) => {
    const count = (xCounts[p.x] = (xCounts[p.x] || 0) + 1);

    // Push badge up if it coincides with Status Date month
    const baseY = -34;

    // If multiple milestones in same month, stack them
    const y = baseY - (count - 1) * 34;

    // If near right edge, shift left so it doesn't get clipped
    const isNearRight = p.x >= categories.length - 2;
    const xOffset = isNearRight ? -40 : 0;

    return {
      value: p.x,
      color: "#BDBDBD",
      width: 1,
      dashStyle: "ShortDash",
      zIndex: 6,
      label: {
        useHTML: true,
        rotation: 0,
        y,          // ✅ badge stays at top
        x: xOffset, // ✅ avoid clipping at right edge
        align: "center",
        text: `
          <div style="
            background:#ffffff;
            padding:6px 10px;
            border-radius:8px;
            box-shadow:0 2px 6px rgba(0,0,0,0.12);
            border:1px solid #E5E7EB;
            text-align:center;
            font-family:Outfit, sans-serif;
            min-width:90px;
            pointer-events:none;
          ">
            <div style="font-size:12px;font-weight:600;color:#111827;">
              ${p.name}
            </div>
            <div style="font-size:11px;font-weight:500;color:#6B7280;margin-top:2px;">
              ${Number(p.y).toFixed(2)} Cr
            </div>
          </div>
        `,
        style: { whiteSpace: "nowrap" },
      },
    };
  });

  const options = useMemo(
    () => ({
      chart: {
        type: "spline",
        backgroundColor: "transparent",
        height: 500,
        spacingRight: 60,
        spacingTop: 80,
      },
      title: { text: "" },
      subtitle: { text: "" },
      credits: { enabled: false },

      xAxis: {
        categories,
        tickLength: 0,
        lineColor: "#010101ff",
        labels: {
          style: {
            fontSize: "14px",
            color: "#333436",
            fontWeight: 500,
            fontFamily: "Outfit, sans-serif",
          },
          formatter: function () {
            if (this.pos === statusIndex)
              return `<span style="font-weight:700;color:#111">${this.value}</span>`;
            return this.value;
          },
          useHTML: true,
        },
        title: {
          text: "Months",
          align: "middle",
          style: {
            color: "#9B9B9B",
            fontSize: "13px",
            fontWeight: 500,
            fontFamily: "Outfit, sans-serif",
          },
          margin: 20,
        },
        plotLines: [
          ...milestonePlotLines,
        ],
      },
      yAxis: {
        title: {
          text: "Financial Progress INR Cr.",
          style: {
            color: "#9B9B9B",
            fontSize: "13px",
            fontWeight: 500,
            fontFamily: "Outfit, sans-serif",
          },
        },
        max: yMax,
        endOnTick: true,
        gridLineColor: "#f3f4f6",
        labels: {
          style: {
            fontSize: "14px",
            color: "#333436",
            fontWeight: 500,
            fontFamily: "Outfit, sans-serif",
          },
        },
        min: 0,
      },

      tooltip: {
        shared: true,
        useHTML: true,
        borderRadius: 8,
        backgroundColor: "#ffffff",
        borderColor: "#e5e7eb",
        style: { fontSize: "12px", color: "#111827" },
        shadow: false,
        padding: 12,
        valueSuffix: " Cr",
        formatter: function () {
          let html = `<div style="font-family:Outfit, sans-serif; color:#111827; line-height:1.5;">`;
          // ${p.series.name}: <b>${(p.y*383/100).toFixed(2)} Cr.</b><br/>

          this.points?.forEach(p => {
            html += `
              <div style="display:flex; align-items:center; gap:8px; white-space:nowrap;">
                <span style="color:${p.color}; font-size:14px; line-height:1;">●</span>
                <span>${p.series.name}: <b>${(p.y).toFixed(2)} Cr.</b></span>
              </div>
            `;
          });

          return `${html}</div>`;
        }
      },

      plotOptions: {
        series: {
          marker: {
            enabled: true,
            radius: 4,
            lineWidth: 1,
          },
          connectNulls: false, // important: do NOT connect across nulls
        },
        spline: {
          lineWidth: 3,
        },
      },

      legend: { enabled: false },

      series: [
        {
          name: "PV-EV Gap (PV>EV)",
          type: "arearange",
          data: gapPositive,
          color: "rgba(49,87,200,0.14)",
          lineWidth: 0,
          marker: { enabled: false },
          enableMouseTracking: false,
          showInLegend: false,
          zIndex: 1
        },
        {
          name: "PV-EV Gap (EV>PV)",
          type: "arearange",
          data: gapNegative,
          color: "rgba(19,138,138,0.14)",
          lineWidth: 0,
          marker: { enabled: false },
          enableMouseTracking: false,
          showInLegend: false,
          zIndex: 1
        },
        {
          name: "PV - Baseline",
          data: bacSeriesLastPoint,
          color: overviewChartPalette.violet,
          marker: { enabled: false },     // ✅ only last point will enable itself
          zIndex: 3,
        },
        {
          name: "BCWS (PV) for the month",
          data: planSeries,
          color: overviewChartPalette.indigo,
          marker: {
            symbol: "circle",
            fillColor: overviewChartPalette.indigo,
          },
          dataLabels: {
            enabled: true,
            formatter: function () {
              return this.point.index === statusIndex
                ? `${this.y.toFixed(2)} Cr.`
                : null;
            },
            style: {
              fontWeight: 'bold',
              color: '#000'
            }
          },
          zIndex: 3
        },
        {
          name: "BCWP (EV)",
          data: evSeries,
          color: overviewChartPalette.teal,
          marker: {
            symbol: "circle",
            fillColor: overviewChartPalette.teal,
          },
          dataLabels: {
            enabled: true,
            formatter: function () {
              return this.point.index === statusIndex
                ? `${this.y.toFixed(2)} Cr.`
                : null;
            },
            style: {
              fontWeight: 'bold',
              color: '#000'
            }
          },
          zIndex: 3
        },
        {
          name: "ACWP (AC)",
          data: acwpSeries,
          color: actualColor,//"#E15759",
          marker: {
            symbol: "circle",
            fillColor: actualColor,//"#E15759",
          },
          dataLabels: {
            enabled: true,
            formatter: function () {
              return this.point.index === statusIndex
                ? `${this.y.toFixed(2)} Cr.`
                : null;
            },
            style: {
              fontWeight: 'bold',
              color: '#000'
            }
          },
          zIndex: 3
        },
        {
          name: "Milestones",
          type: "scatter",
          data: milestonePoints,
          color: overviewChartPalette.amber,
          marker: {
            symbol: "circle",
            radius: 6,
            fillColor: overviewChartPalette.amber,
            lineColor: "#ffffff",
            lineWidth: 2,
          },
          dataLabels: { enabled: false }, // ✅ badge is now on plotLine
          zIndex: 50,                     // ✅ ensure dot stays above other lines
        },
        {
          name: "Projected",
          data: projSeriesLastPoint,
          dashStyle: "Dash",
          lineWidth: 2,
          color: overviewChartPalette.violet,
          marker: { enabled: false },     // ✅ only last point will enable itself
          zIndex: 2,
        }
      ],
    }),
    [categories, milestones, planSeries, evSeries, acwpSeries, evCumSeries, acwpCumSeries, bacSeries, projectedSeries]
  );

  return (
    <Card sx={{ borderRadius: 3, border: "1px solid #E4E4E4", pb: 2 }}>
      {/* Header */}
      <Box sx={{ borderBottom: "1px solid #E4E4E4", p: 1.5, display: "flex", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "20px", fontFamily: "Outfit" }}>
            EVM Management Surve
          </Typography>
          <Typography sx={{ fontWeight: 300, fontSize: "16px", color: "#7D7F81" }}>
            Compare planned, earned, actual, and forecast cost movement over time.
          </Typography>
        </Box>

        <Stack sx={{ mr: "30px" }}>
          <LegendItem color={overviewChartPalette.violet} label="PV - Baseline" />
          <LegendItem color={overviewChartPalette.indigo} label="BCWS (PV) for the month" />
          <LegendItem color={overviewChartPalette.teal} label="BCWP (EV)" />

        </Stack>
        <Stack sx={{ mr: "30px" }}>
          <LegendItem color={actualColor} label="ACWP (AC)" />
          <LegendItem color={overviewChartPalette.violet} label="Forecast" dashed />
          {/* <LegendItem color="#F28E2B" label="ETC" />
                <LegendItem color="#9467BD" label="EAC" /> */}
        </Stack>
      </Box>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Card>
  );
};

export default SCurveCostManagement;
